import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Category} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/category.types';
import {FilterSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {GetFilterResponse as BrandShopGetFiltersResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {GetFiltersResponse as CategoryGetFiltersResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/filters/service.types';
import {GetFiltersResponse as CouponLandingPageGetFiltersResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coupons/v1/landingpage/service.types';
import {
  FilterLabels,
  FilterSelectedValue,
  FilterType,
} from '@aboutyou/legacy-tracking-sdk';
import {
  useTrackFilter,
  useTrackOnCloseFilter,
  useTrackOnOpenFilter,
} from '@aboutyou/tracking';
import {ErrorBoundary, FilterBar} from '@aboutyou/ui-components';
import React, {FC, useRef} from 'react';
import styled from 'styled-components';
import {usePotentialFilters} from '../../ProductStream/utils';
import {SetFilterObject} from '../../ProductStream/utils/usePotentialFilters';
import {PersonalizedFilters} from '../PersonalizedFilters/PersonalizedFilters';
import {SearchFilter} from '../SearchFilter/SearchFilter';
import {FilterSectionAttribute} from './FilterSections/FilterSectionAttribute';
import {FilterSectionBoolean} from './FilterSections/FilterSectionBoolean';
import {FilterSectionBrand} from './FilterSections/FilterSectionBrands';
import {FilterSectionColor} from './FilterSections/FilterSectionColor';
import {FilterSectionDiscount} from './FilterSections/FilterSectionDiscount';
import {FilterSectionGroupedAttributes} from './FilterSections/FilterSectionGroupedAttributes';
import {FilterSectionMaterial} from './FilterSections/FilterSectionMaterial';
import {FilterSectionPrice} from './FilterSections/FilterSectionPrice';
import {FilterSectionSizeGrouped} from './FilterSections/FilterSectionSizeGrouped';
import {FilterSectionSizeSimple} from './FilterSections/FilterSectionSizeSimple';
import {FilterSectionYourDeals} from './FilterSections/FilterSectionYourDeals';
import {ResetAllFilters} from './ResetAllFilters';
import {useAppliedFiltersData} from './helpers';

export interface InlineFilterProps {
  inlineFiltersRef: React.RefObject<HTMLDivElement | null>;
  showPersonalizedFilters?: boolean;
  category?: Category;
}

type ModifiedSetFilterObject = Omit<
  SetFilterObject,
  'label' | 'shouldTrackImmediately'
>;

export const InlineFilter: FC<
  (
    | CategoryGetFiltersResponse
    | BrandShopGetFiltersResponse
    | CouponLandingPageGetFiltersResponse
  ) &
    InlineFilterProps
> = ({
  filters,
  inlineFiltersRef,
  appliedFiltersCount,
  showPersonalizedFilters,
  category,
}) => {
  const filterBarRef = useRef<HTMLDivElement | null>(null);

  const {
    filters: activeFilters,
    resetFilters,
    resetFilter,
    setFilter,
    setFilters,
    submitFiltersInBulk,
  } = usePotentialFilters();

  const trackOnCloseFilter = useTrackOnCloseFilter();
  const trackOnOpenFilter = useTrackOnOpenFilter();
  const trackFilter = useTrackFilter();

  const onCloseFilter = (
    filterSlug: string,
    label: FilterLabels,
    selectedValue?: FilterSelectedValue,
    filterType?: FilterType,
    trackSubmit?: boolean,
  ) => {
    submitFiltersInBulk();
    trackOnCloseFilter(filterSlug, label, selectedValue, filterType);
    if (trackSubmit && selectedValue && filterType) {
      trackFilter(filterSlug, selectedValue, filterType, 'submit', label);
    }
  };

  const onSetBooleanFilter = (filterObj: ModifiedSetFilterObject) => {
    setFilter({
      ...filterObj,
      label: 'quick_filters',
      shouldTrackImmediately: true,
    });
  };

  const onSetFilter = (filterObj: ModifiedSetFilterObject) => {
    setFilter({
      ...filterObj,
      label: 'quick_filters',
      shouldTrackImmediately: false,
    });
  };

  const onSetFilters = (filterObj: Array<SetFilterObject>) => {
    setFilters(filterObj);
  };

  const personalizedFiltersFeatureFlag = useIsFeatureActive(
    'personal_filters_feature',
  );

  const isPersonalizedFilterShown =
    Boolean(showPersonalizedFilters) && personalizedFiltersFeatureFlag.isActive;

  const appliedFilters = useAppliedFiltersData({
    filters,
    activeFilters,
  });

  return (
    <>
      {filters.length > 0 && (
        <>
          <FilterBarWrapper
            ref={inlineFiltersRef}
            $isPersonalizedFiltersShown={isPersonalizedFilterShown}
          >
            <FilterBar
              ref={filterBarRef}
              isInitiallyExpanded={appliedFiltersCount > 0}
              alwaysRenderedContent={
                <ResetAllFilters appliedFiltersCount={appliedFiltersCount} />
              }
            >
              {filters.map((filter: FilterSection, i: number) => {
                if (!filter?.type?.$case) return null;

                switch (filter.type.$case) {
                  case 'boolean': {
                    const booleanFilterData = filter.type.boolean;
                    const filterId = booleanFilterData.slug;
                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-boolean-${filterId}`}
                        renderContext={`category_filter_section_boolean-${filterId}`}
                      >
                        <FilterSectionBoolean
                          activeFilters={activeFilters}
                          data={booleanFilterData}
                          setFilter={onSetBooleanFilter}
                          resetFilter={resetFilter}
                        />
                      </ErrorBoundary>
                    );
                  }
                  case 'material': {
                    const materialFilterData = filter.type.material;
                    const filterId = materialFilterData.materialFilter?.slug;
                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-flyout-section-material-${filterId}`}
                        renderContext={`category_filter_section_material-${filterId}`}
                      >
                        <FilterSectionMaterial
                          activeFilters={activeFilters}
                          data={materialFilterData}
                          setFilter={onSetFilter}
                          resetFilter={resetFilter}
                          onOpen={trackOnOpenFilter}
                          filterLabel="quick_filters"
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );
                  }
                  case 'attribute': {
                    const attributeFilterData = filter.type.attribute;
                    const filterId = attributeFilterData.slug;

                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-flyout-section-attribute-${filterId}`}
                        renderContext={`category_filter_section_attribute-${filterId}`}
                      >
                        <FilterSectionAttribute
                          activeFilters={activeFilters}
                          data={attributeFilterData}
                          setFilter={onSetFilter}
                          filterLabel="quick_filters"
                          resetFilter={resetFilter}
                          isSingleSelect={attributeFilterData.isSingleSelect}
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );
                  }
                  case 'brand': {
                    const brandFilterData = filter.type.brand;
                    const filterId = brandFilterData.slug;
                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-brand-${filterId}`}
                        renderContext={`category_filter_section_brand-${filterId}`}
                      >
                        <FilterSectionBrand
                          activeFilters={activeFilters}
                          data={brandFilterData}
                          setFilter={onSetFilter}
                          filterLabel="quick_filters"
                          resetFilters={resetFilters}
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                          appliedFilters={appliedFilters}
                        />
                      </ErrorBoundary>
                    );
                  }
                  case 'color': {
                    const colorFilterData = filter.type.color;
                    const filterId = colorFilterData.slug;
                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-color-${filterId}`}
                        renderContext={`category_filter_section_color-${filterId}`}
                      >
                        <FilterSectionColor
                          activeFilters={activeFilters}
                          data={colorFilterData}
                          setFilter={onSetFilter}
                          filterLabel="quick_filters"
                          resetFilter={resetFilter}
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );
                  }
                  case 'discountLevel': {
                    const discountFilterData = filter.type.discountLevel;
                    const filterId = discountFilterData?.slug;

                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-price-${filterId}`}
                        renderContext={`category_filter_section_price-${filterId}`}
                      >
                        <FilterSectionDiscount
                          activeFilters={activeFilters}
                          data={discountFilterData}
                          resetFilters={resetFilters}
                          setFilter={onSetFilter}
                          filterLabel="quick_filters"
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );
                  }
                  case 'price': {
                    const priceFilterData = filter.type.price;
                    const filterId = priceFilterData.priceSlider?.slug;
                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-price-${filterId}`}
                        renderContext={`category_filter_section_price-${filterId}`}
                      >
                        <FilterSectionPrice
                          activeFilters={activeFilters}
                          data={priceFilterData}
                          resetFilters={resetFilters}
                          setFilter={onSetFilter}
                          filterLabel="quick_filters"
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );
                  }

                  case 'groupedSize':
                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-groupedSize-${i}`}
                        renderContext="category_filter_section_groupSize"
                      >
                        <FilterSectionSizeGrouped
                          activeFilters={activeFilters}
                          data={filter.type.groupedSize}
                          resetFilter={resetFilter}
                          resetFilters={resetFilters}
                          setFilter={onSetFilter}
                          filterLabel="quick_filters"
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );

                  case 'simpleSize':
                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-simpleSize-${i}`}
                        renderContext="category_filter_section_simpleSize"
                      >
                        <FilterSectionSizeSimple
                          activeFilters={activeFilters}
                          data={filter.type.simpleSize}
                          setFilter={onSetFilter}
                          resetFilter={resetFilter}
                          filterLabel="quick_filters"
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );
                  case 'yourDeals':
                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-yourDeals-${i}`}
                        renderContext="category_filter_section_yourDeals"
                      >
                        <FilterSectionYourDeals
                          activeFilters={activeFilters}
                          data={filter.type.yourDeals}
                          setFilter={onSetBooleanFilter}
                          setFilters={onSetFilters}
                          resetFilter={resetFilter}
                          resetFilters={resetFilters}
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );

                  case 'groupedAttributes': {
                    const groupedAttributesData = filter.type.groupedAttributes;
                    const filterId = groupedAttributesData.slug;

                    return (
                      <ErrorBoundary
                        ErrorComponent={ErrorComponent}
                        key={`filter-dropdown-section-grouped-attribute-${filterId}`}
                        renderContext={`category_filter_section_grouped_attribute-${filterId}`}
                      >
                        <FilterSectionGroupedAttributes
                          activeFilters={activeFilters}
                          data={groupedAttributesData}
                          setFilter={onSetFilter}
                          filterLabel="quick_filters"
                          resetFilter={resetFilter}
                          onOpen={trackOnOpenFilter}
                          onClose={onCloseFilter}
                        />
                      </ErrorBoundary>
                    );
                  }

                  default:
                    return null;
                }
              })}
            </FilterBar>
            {filters.map((filter: FilterSection, i) => {
              if (filter.type?.$case === 'appliedFilter') {
                return (
                  <ErrorBoundary
                    ErrorComponent={ErrorComponent}
                    renderContext="category_search_filter"
                    key={`searchFilterSection-${i}`}
                  >
                    <StyledSearchFilter
                      filters={filter.type.appliedFilter.values}
                      disableHorizontalList
                    />
                  </ErrorBoundary>
                );
              }

              return null;
            })}
          </FilterBarWrapper>
          {isPersonalizedFilterShown && (
            <PersonalizedFilters
              appliedFiltersData={appliedFilters}
              categoryName={category!.name}
            />
          )}
        </>
      )}
    </>
  );
};

const FilterBarWrapper = styled.div<{$isPersonalizedFiltersShown: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  ${({$isPersonalizedFiltersShown}) =>
    `margin-bottom: ${$isPersonalizedFiltersShown ? '1rem' : '1.875rem'};`}
`;

const StyledSearchFilter = styled(SearchFilter)`
  padding: 0;
`;

const ErrorComponent = () => null;
